import { useState, useRef } from "react";
import { NavLink } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Send, Loader2, AlertTriangle, ChevronRight, Search, ImagePlus, X } from "lucide-react";

/**
 * "Ask Asset IQ" chatbox — for Dashboard.
 */
export function AskAssetIQ() {
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const ask = async (e) => {
    e?.preventDefault();
    const q = question.trim();
    if (!q || loading) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setQuestion("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", { question: q, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "ai", text: data.answer }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "ai", text: "Sorry, I couldn't answer that. " + (formatApiError(err.response?.data?.detail) || "") }]);
    } finally { setLoading(false); }
  };

  const suggestions = [
    "How many assets are active?",
    "What's my open PO count?",
    "Any consumables below reorder level?",
  ];

  return (
    <Card data-testid="ask-assetiq-card" className="border-[#f5c4d3] bg-gradient-to-br from-white to-[#fcf0f3]/40">
      <CardHeader className="border-b border-[#f5c4d3]/60">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#7C1D3A]" /> Ask Asset IQ
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-xs text-slate-500 space-y-2">
            <p>Ask anything in plain English about your registry. Try:</p>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => setQuestion(s)}
                  className="text-xs bg-white border border-slate-200 rounded-full px-3 py-1 hover:border-[#7C1D3A] hover:text-[#7C1D3A]"
                  data-testid={`ask-suggest-${s.slice(0, 10)}`}
                >{s}</button>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-2 text-sm" data-testid="ask-messages">
            {messages.map((m, i) => (
              <div key={i} className={`p-2.5 rounded-lg ${m.role === "user" ? "bg-[#7C1D3A] text-white ml-8" : "bg-white border border-slate-200 mr-8"}`}>
                <div className="whitespace-pre-wrap">{m.text}</div>
              </div>
            ))}
            {loading && (
              <div className="p-2.5 rounded-lg bg-white border border-slate-200 mr-8 text-slate-500 text-xs flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking...
              </div>
            )}
          </div>
        )}
        <form onSubmit={ask} className="flex gap-2">
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask Asset IQ..."
            disabled={loading}
            data-testid="ask-input"
          />
          <Button type="submit" disabled={loading || !question.trim()} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="ask-submit">
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}


/**
 * Predictive Insights — dashboard card showing top at-risk assets.
 */
export function PredictiveInsights() {
  const [state, setState] = useState({ loading: false, loaded: false, data: null });

  const scan = async () => {
    setState({ loading: true, loaded: false, data: null });
    try {
      const { data } = await api.get("/ai/predictive-scan");
      setState({ loading: false, loaded: true, data });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Predictive scan failed");
      setState({ loading: false, loaded: true, data: null });
    }
  };

  return (
    <Card data-testid="predictive-insights-card">
      <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-[#7C1D3A]" /> Predictive Insights
        </CardTitle>
        <Button size="sm" variant="outline" onClick={scan} disabled={state.loading} data-testid="run-predictive-scan">
          {state.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
          {state.loaded ? "Rescan" : "Run Scan"}
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {!state.loaded ? (
          <div className="p-8 text-center text-sm text-slate-500">
            <Sparkles className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            Run a predictive scan to see assets at risk (aging, overdue maintenance, expiring AMC, recent breakdowns).
          </div>
        ) : !state.data || state.data.at_risk_count === 0 ? (
          <div className="p-8 text-center text-sm text-emerald-700">
            No at-risk assets right now.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {state.data.assets.map((a) => (
              <NavLink key={a.id} to={`/assets/${a.id}`} className="flex items-center justify-between px-6 py-3 hover:bg-slate-50 transition-colors">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{a.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{a.asset_id} · {a.category}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{a.reasons.join(" · ")}</div>
                </div>
                <div className="text-right shrink-0 ml-4">
                  <Badge variant="outline" className={
                    a.risk_level === "high" ? "border-red-300 bg-red-50 text-red-700"
                      : a.risk_level === "medium" ? "border-amber-300 bg-amber-50 text-amber-800"
                        : "border-slate-300 bg-slate-50 text-slate-700"
                  }>
                    {a.risk_level.toUpperCase()} · {a.risk_score}
                  </Badge>
                </div>
              </NavLink>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


/**
 * Smart Search bar — natural-language asset search using LLM to build filter.
 */
export function SmartSearchBar({ onResults, onClear }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    const query = q.trim();
    if (!query || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/smart-search", { query });
      setMeta({ interpretation: data.interpretation, count: data.count, filter: data.filter_applied });
      onResults(data.results, query);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Smart search failed");
    } finally { setLoading(false); }
  };

  const clear = () => {
    setQ(""); setMeta(null); onClear();
  };

  return (
    <div data-testid="smart-search-bar">
      <form onSubmit={search} className="flex gap-2">
        <div className="relative flex-1">
          <Sparkles className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7C1D3A]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder='Ask in plain English — e.g. "machinery with AMC expiring in 30 days"'
            className="pl-9"
            disabled={loading}
            data-testid="smart-search-input"
          />
        </div>
        <Button type="submit" disabled={loading || !q.trim()} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="smart-search-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}
          Ask AI
        </Button>
        {meta && (
          <Button type="button" variant="outline" onClick={clear} data-testid="smart-search-clear"><X className="w-4 h-4" /></Button>
        )}
      </form>
      {meta && (
        <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
          <Sparkles className="w-3.5 h-3.5 text-[#7C1D3A]" />
          <span className="italic">{meta.interpretation || "AI-filtered results"}</span>
          <Badge variant="outline">{meta.count} match{meta.count === 1 ? "" : "es"}</Badge>
        </div>
      )}
    </div>
  );
}


/**
 * Photo Auto-Fill button — for AssetForm. Accepts a File and calls the /ai/photo-analyze endpoint.
 */
export function PhotoAutoFill({ onAnalyzed }) {
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setLoading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const { data } = await api.post("/ai/photo-analyze", { image_base64: b64 });
      onAnalyzed(data);
      toast.success("Fields auto-filled from photo");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Photo analysis failed");
    } finally { setLoading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="photo-input"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={loading}
        className="border-[#f5c4d3] text-[#7C1D3A] hover:bg-[#fcf0f3]"
        data-testid="photo-autofill-btn"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ImagePlus className="w-3.5 h-3.5 mr-1" />}
        Auto-fill from Photo (AI)
      </Button>
    </>
  );
}
